// Signatureinheiten der Kassen (je Betriebsstätte eine Einheit, je Kasse ein Client), Selbsttest, Ausfälle,
// Datenblatt für die Mitteilung je Betriebsstätte, Archiv-Export der Einheit und der Export für die Betriebsprüfung.
// Zugangsdaten gehen nur hinein (versiegelt gespeichert) und kommen nie heraus.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zDay, zLocationRef } from './common'
import { zOutageOfflineFields } from './offline'
import { zFiscalMode } from './views'
import { brand } from '@shared/brand'

const READ = 'pos.shift.read'
const MANAGE = 'pos.fiscal.manage'
const REGISTERS = 'pos.register.manage'
const EXPORT = 'pos.export.create'

export const FISCAL_ENVS = ['demo', 'simulator', 'test', 'live'] as const
export const zFiscalEnv = z.enum(FISCAL_ENVS)

export const zSelfTest = z.object({ at: z.date(), ok: z.boolean(), problems: z.array(z.string()) })

export const zFiscalRegister = z.object({
  id: zId,
  code: z.string(),
  name: z.string(),
  active: z.boolean(),
  /** Seriennummer der Kasse gegenüber der Einheit */
  serial: z.string().nullable(),
  clientId: z.string().nullable(),
  clientState: z.string().nullable(),
  openShift: z.boolean(),
  mode: zFiscalMode,
})

export const zFiscalUnitRow = z.object({
  id: zId,
  provider: z.string(),
  env: zFiscalEnv,
  /** Seriennummer der Einheit (64 Hexzeichen; Demo: DEMO-…) */
  serial: z.string(),
  state: z.string(),
  location: zLocationRef.nullable(),
  description: z.string().nullable(),
  certificationId: z.string().nullable(),
  algorithm: z.string().nullable(),
  timeFormat: z.string().nullable(),
  publicKey: z.string().nullable(),
  initializedAt: z.date().nullable(),
  selfTest: zSelfTest.nullable(),
  outage: z.object({ since: z.date(), reason: z.string(), count: z.int() }).nullable(),
  /** Schulung im Demo-Betrieb: Der Simulator dieser Einheit ist abgeschaltet („Ausfall üben“) */
  simulatorOffline: z.boolean(),
  registers: z.array(zFiscalRegister),
})

export const fiscalOverview = defineContract({
  name: 'pos.fiscal.overview',
  title: 'Signatureinheiten und Kassen anzeigen',
  description: 'Je Betriebsstätte die Signatureinheit mit Zustand, Seriennummer, Kennung des Zertifikats und Selbsttest; je Kasse der Client.',
  llm: {
    description:
      'Liefert je Markt die Signatureinheit der Kassen (Anbieter, Umgebung demo/simulator/test/live, Zustand, ' +
      'Seriennummer, Kennung des Zertifikats, letzter Selbsttest, laufender Ausfall) und je Kasse ihre ' +
      'Seriennummer, die Anmeldung an der Einheit und die Betriebsart (live, test, demo, training). Zugangsdaten ' +
      'stehen nie in der Antwort.',
    keywords: ['Signatureinheit', 'Seriennummer Kasse', 'Selbsttest', 'Kasseneinrichtung'],
  },
  input: z.strictObject({}),
  output: z.object({
    tenantDemo: z.boolean(),
    simulatorAvailable: z.boolean(),
    liveAvailable: z.boolean(),
    units: z.array(zFiscalUnitRow),
    /** Märkte mit Kassen – auch solche ohne eigene Einheit; registerCount = Kassen in Betrieb */
    locations: z.array(z.object({ location: zLocationRef, unitId: zId.nullable(), registerCount: z.int() })),
    /**
     * Wächter: Der Abgleich im Hintergrund ist seit diesem Zeitpunkt fällig, läuft aber nicht (Job-Läufer steht) –
     * nachgeholte Absicherungen und verwaiste Vorgänge bleiben liegen. null = läuft.
     */
    reconcileStalledSince: z.date().nullable(),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

export const fiscalUnitSetup = defineContract({
  name: 'pos.fiscal.unit.setup',
  title: 'Signatureinheit einrichten',
  description: 'Richtet für einen Markt die Signatureinheit ein (Simulator für Schulung und Demo oder die Einheit des Anbieters) und meldet alle Kassen des Markts an.',
  llm: { description: 'Richtet die Signatureinheit eines Markts ein. Nur für die Inhaberin bzw. den Inhaber in den Einstellungen der Kasse.', keywords: ['Signatureinheit einrichten'] },
  input: z
    .strictObject({
      locationId: zId,
      /** simulator: Schulung und Demo · test: Testumgebung des Anbieters · live: Echtbetrieb */
      env: z.enum(['simulator', 'test', 'live'], { error: 'Bitte wählen Sie Simulator, Testumgebung oder Echtbetrieb.' }),
      /** Zugangsdaten des Anbieters (nur test/live) – werden versiegelt gespeichert und nie wieder angezeigt */
      apiKey: z.string().trim().min(8, { error: 'Bitte geben Sie den API-Schlüssel ein.' }).max(200).optional(),
      apiSecret: z.string().trim().min(8, { error: 'Bitte geben Sie das API-Secret ein.' }).max(200).optional(),
      description: z.string().trim().max(80).optional(),
    })
    .refine((value) => value.env === 'simulator' || (Boolean(value.apiKey) && Boolean(value.apiSecret)), { error: 'Für die Einheit des Anbieters braucht es API-Schlüssel und API-Secret.', path: ['apiKey'] }),
  output: z.object({ unitId: zId, serial: z.string(), registered: z.int(), selfTestOk: z.boolean(), message: z.string() }),
  permission: MANAGE,
  risk: 'destructive',
  effects: { securitySensitive: true },
  scope: (input) => [input.locationId],
  assistant: 'never',
})

export const fiscalSelfTest = defineContract({
  name: 'pos.fiscal.selftest',
  title: 'Selbsttest der Signatureinheit',
  description: 'Prüft Anmeldung, Zustand, Zertifikat, Kapazität und die Anmeldung der Kassen an der Einheit.',
  llm: { description: 'Startet den Selbsttest einer Signatureinheit und hält das Ergebnis fest.', keywords: ['Selbsttest'] },
  input: z.strictObject({ unitId: zId }),
  output: z.object({ ok: z.boolean(), problems: z.array(z.string()), message: z.string() }),
  permission: REGISTERS,
  risk: 'write',
  scope: 'record',
  assistant: 'never',
})

export const fiscalRegisterClient = defineContract({
  name: 'pos.fiscal.registerClient',
  title: 'Kasse an der Signatureinheit anmelden',
  description: 'Meldet eine Kasse an der eingerichteten Signatureinheit ihres Markts an – für Kassen, die nach der Einrichtung angelegt wurden oder deren Anmeldung scheiterte.',
  llm: { description: 'Meldet eine Kasse an der Signatureinheit ihres Markts an. Nur für die Inhaberin bzw. den Inhaber in den Einstellungen der Kasse.', keywords: ['Kasse anmelden'] },
  input: z.strictObject({ registerId: zId }),
  output: z.object({ registerId: zId, registered: z.boolean(), message: z.string() }),
  permission: MANAGE,
  risk: 'write',
  effects: { securitySensitive: true },
  scope: 'record',
  assistant: 'never',
})

export const zOutageRow = z.object({
  id: zId,
  unitSerial: z.string(),
  env: z.string(),
  location: zLocationRef.nullable(),
  register: z.object({ id: zId, code: z.string(), name: z.string() }).nullable(),
  from: z.date(),
  to: z.date().nullable(),
  /** Dauer in Minuten (bis jetzt, wenn er noch läuft) */
  minutes: z.int(),
  reason: z.string(),
  kind: z.string().nullable(),
  code: z.string().nullable(),
  requestId: z.string().nullable(),
  /** Vorgänge, die (ganz oder teilweise) ohne Absicherung liefen */
  vorgangCount: z.int(),
  /** Art (Ausfall der Einheit bzw. ohne Verbindung je Gerät), Gerät, Lage, Gerätezeiten, dokumentiert (§14.2) */
  ...zOutageOfflineFields,
})

export const fiscalOutageList = defineContract({
  name: 'pos.fiscal.outages',
  title: 'Ausfälle der Signatureinheit',
  description: 'Protokoll der Ausfälle: von, bis, Dauer, Grund, betroffene Vorgänge.',
  llm: {
    description: 'Listet die Ausfälle der Signatureinheiten (neueste zuerst): Markt, Kasse, von, bis, Dauer in Minuten, Grund und wie viele Vorgänge ohne Absicherung liefen. Verwenden für „gab es Ausfälle der Kasse“.',
    keywords: ['Ausfall', 'Kassenausfall', 'ohne Absicherung'],
  },
  input: z.strictObject({ locationId: zId.optional(), from: zDay.optional(), to: zDay.optional() }),
  output: z.object({ rows: z.array(zOutageRow) }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

export const fiscalSimulateOutage = defineContract({
  name: 'pos.fiscal.simulateOutage',
  title: 'Ausfall der Signatureinheit üben',
  description: 'Schulung im Demo-Betrieb: schaltet den Simulator eines Markts ab bzw. wieder an.',
  llm: { description: 'Schaltet im Demo-Betrieb den Simulator der Signatureinheit eines Markts ab oder an (Schulung „Ausfall“).', keywords: ['Ausfall üben'] },
  input: z.strictObject({ unitId: zId, offline: z.boolean() }),
  output: z.object({ offline: z.boolean(), message: z.string() }),
  permission: REGISTERS,
  risk: 'write',
  scope: 'record',
  assistant: 'never',
})

export const fiscalElsterSheet = defineContract({
  name: 'pos.fiscal.elsterSheet',
  title: 'Datenblatt für die Mitteilung der Kassen',
  description: `Alle Angaben, die der Betrieb für die Mitteilung seiner Kassen je Betriebsstätte über ELSTER braucht. ${brand.name} übermittelt nichts.`,
  llm: {
    description: `Liefert das Datenblatt einer Betriebsstätte für die Mitteilung der Kassen über ELSTER: Steuernummer, Anschrift, Kassen mit Seriennummer, Software, Anschaffung und Inbetriebnahme, Signatureinheit mit Seriennummer und Kennung des Zertifikats. ${brand.name} übermittelt die Mitteilung nicht.`,
    keywords: ['ELSTER', 'Mitteilung Kassen', 'Kassenmeldung', 'Datenblatt'],
  },
  input: z.strictObject({ locationId: zId }),
  output: z.object({
    demo: z.boolean(),
    company: z.object({ name: z.string(), taxNumber: z.string().nullable(), address: z.array(z.string()) }),
    location: z.object({ id: zId, name: z.string(), address: z.array(z.string()) }),
    registerCount: z.int(),
    registers: z.array(
      z.object({
        code: z.string(),
        name: z.string(),
        kind: z.string(),
        software: z.string(),
        softwareVersion: z.string(),
        serial: z.string().nullable(),
        manufacturer: z.string(),
        model: z.string(),
        acquiredAt: z.string().nullable(),
        commissionedAt: z.string().nullable(),
        decommissionedAt: z.string().nullable(),
        decommissionReason: z.string().nullable(),
      }),
    ),
    unit: z
      .object({
        serial: z.string(),
        certificationId: z.string().nullable(),
        commissionedAt: z.string().nullable(),
        kind: z.string(),
        env: z.string(),
      })
      .nullable(),
    hints: z.array(z.string()),
  }),
  permission: READ,
  risk: 'read',
  scope: (input) => [input.locationId],
})

export const zTarExportRow = z.object({
  id: zId,
  unitSerial: z.string(),
  from: z.date(),
  to: z.date(),
  state: z.string(),
  error: z.string().nullable(),
  fileName: z.string().nullable(),
  sha256: z.string().nullable(),
  sizeBytes: z.int().nullable(),
  createdAt: z.date(),
})

export const tarExportStart = defineContract({
  name: 'pos.fiscal.tarExport.start',
  title: 'Export der Signatureinheit anstoßen',
  description: 'Fordert bei der Signatureinheit den Export (TAR) eines Zeitraums an.',
  llm: { description: 'Fordert den Archiv-Export einer Signatureinheit für einen Zeitraum an.', keywords: ['TAR-Export'] },
  input: z.strictObject({ unitId: zId, from: zDay, to: zDay }),
  output: z.object({ id: zId, state: z.string(), message: z.string() }),
  permission: EXPORT,
  risk: 'write',
  scope: 'record',
  assistant: 'never',
})

export const tarExportRefresh = defineContract({
  name: 'pos.fiscal.tarExport.refresh',
  title: 'Export der Signatureinheit abholen',
  description: 'Fragt den Zustand eines Exports ab und holt die fertige Datei.',
  llm: { description: 'Fragt den Zustand eines Archiv-Exports ab und holt die fertige Datei ab.', keywords: ['TAR-Export'] },
  input: z.strictObject({ id: zId }),
  output: z.object({ id: zId, state: z.string(), message: z.string() }),
  permission: EXPORT,
  risk: 'write',
  scope: 'record',
  assistant: 'never',
})

export const tarExportDownload = defineContract({
  name: 'pos.fiscal.tarExport.download',
  title: 'Export der Signatureinheit herunterladen',
  description: 'Die abgeholte Datei eines Exports (Base64) mit SHA-256.',
  llm: { description: 'Liefert eine abgeholte Exportdatei einer Signatureinheit zum Herunterladen.', keywords: ['TAR-Export'] },
  input: z.strictObject({ id: zId }),
  output: z.object({ fileName: z.string(), contentType: z.string(), base64: z.string(), sha256: z.string() }),
  permission: EXPORT,
  risk: 'read',
  scope: 'record',
  assistant: 'never',
})

export const zAuditExportRow = z.object({
  id: zId,
  registers: z.array(z.object({ id: zId, code: z.string(), name: z.string() })),
  from: z.string(),
  to: z.string(),
  fileName: z.string(),
  sha256: z.string(),
  sizeBytes: z.int(),
  fileCount: z.int(),
  closingCount: z.int(),
  requestedBy: z.string().nullable(),
  createdAt: z.date(),
})

export const auditExportList = defineContract({
  name: 'pos.export.list',
  title: 'Exporte für die Betriebsprüfung',
  description: 'Bisherige Exporte der Kassendaten und der Signatureinheiten, dazu die Kassen und Einheiten zur Auswahl.',
  llm: {
    description: 'Listet die bisherigen Exporte der Kassendaten (Kassen, Zeitraum, Datei, SHA-256, wer, wann) und der Signatureinheiten sowie die Kassen und Einheiten, für die exportiert werden kann.',
    keywords: ['Export Betriebsprüfung', 'Kassendaten exportieren', 'Prüfer'],
  },
  input: z.strictObject({}),
  output: z.object({
    exports: z.array(zAuditExportRow),
    tarExports: z.array(zTarExportRow),
    registers: z.array(z.object({ id: zId, code: z.string(), name: z.string(), location: zLocationRef, lastClosing: z.date().nullable() })),
    units: z.array(z.object({ id: zId, serial: z.string(), env: z.string(), location: zLocationRef.nullable() })),
  }),
  permission: EXPORT,
  risk: 'read',
  scope: 'any',
})

export const auditExportCreate = defineContract({
  name: 'pos.export.create',
  title: 'Kassendaten für die Betriebsprüfung exportieren',
  description: 'Erstellt das Paket der Kassendaten (alle Abschlüsse der gewählten Kassen im Zeitraum) und hält fest, wer es wann erstellt hat.',
  llm: { description: 'Erstellt das Export-Paket der Kassendaten für die gewählten Kassen und den Zeitraum. Nur über die Seite der Exporte.', keywords: ['Export Betriebsprüfung'] },
  input: z
    .strictObject({ registerIds: z.array(zId).min(1, { error: 'Bitte wählen Sie mindestens eine Kasse.' }).max(50), from: zDay, to: zDay })
    .refine((value) => value.from <= value.to, { error: 'Das Ende des Zeitraums liegt vor seinem Beginn.', path: ['to'] }),
  output: z.object({
    id: zId,
    fileName: z.string(),
    sha256: z.string(),
    sizeBytes: z.int(),
    closingCount: z.int(),
    vorgangCount: z.int(),
    skippedClosings: z.int(),
    demo: z.boolean(),
    files: z.array(z.object({ name: z.string(), rows: z.int(), bytes: z.int(), sha256: z.string() })),
    message: z.string(),
  }),
  permission: EXPORT,
  risk: 'write',
  scope: 'record',
  assistant: 'never',
})

export const auditExportDownload = defineContract({
  name: 'pos.export.download',
  title: 'Export für die Betriebsprüfung herunterladen',
  description: 'Erzeugt das Paket eines Exports byte-gleich neu und liefert es (Base64) mit SHA-256.',
  llm: { description: 'Liefert die Datei eines bereits erstellten Exports der Kassendaten zum Herunterladen.', keywords: ['Export herunterladen'] },
  input: z.strictObject({ id: zId }),
  output: z.object({ fileName: z.string(), contentType: z.string(), base64: z.string(), sha256: z.string() }),
  permission: EXPORT,
  risk: 'read',
  scope: 'record',
  assistant: 'never',
})
